import { ApolloError, gql, QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import Checkbox from '../../../../common/src/components/Checkbox'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { Link } from '../../../../common/src/components/Link'
import TableMultiselect from '../../../../common/src/components/TableMultiselect'
import Toggle from '../../../../common/src/components/Toggle'
import {
  AlignRight,
  ColoredSpan,
  EllipsisDiv,
  ErrorText,
  H1,
  H3,
  H5,
  InputMed,
  SmallerTextSpan,
  Spacer,
} from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { formatEnum } from '../../../../common/src/format'
import { getState, lex } from '../../../../common/src/func'
import {
  BulkUpdateOp,
  FetchTasksAndPropertiesQuery,
  FetchTasksAndPropertiesQueryVariables,
  UpdateTasksMutation,
} from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { apolloWebClient } from '../../apolloClientForWeb'
import { bulkUpdateTasks } from '../../graphql/mutations'
import { fetchTasksAndPropertiesQuery } from '../../graphql/queries/queries'
import { TasksTable } from '../components/TasksTable'
import { getTasksNotUpdated, renderFieldError, renderTasksList, Tab } from './BulkUpdatePage'

type LeasingTask = UpdateTasksMutation['bulkUpdateTasks'][0]
type PmProp = FetchTasksAndPropertiesQuery['firm']['pmPropertiesWithoutTasks'][0]

const firmProperties = gql`
  fragment FirmProperties on Firm {
    id
    pmPropertiesWithoutTasks {
      id
      displayName
      auditKeyJson
      jsonUrl
      pmUrl
      numUnits
      numUnitsUnrented
      cleanAddress
    }
  }
`

const createTasksMutation = gql`
  mutation CreateTasks($firmId: Int!, $pmItemKeyJsons: [String!]!, $launchDate: String!) {
    createTasks(firmId: $firmId, pmItemKeyJsons: $pmItemKeyJsons, launchDate: $launchDate) {
      ...FirmProperties
    }
  }
  ${firmProperties}
`

const createMergedTaskMutation = gql`
  mutation CreateMergedTask($firmId: Int!, $pmItemKeyJsons: [String!]!, $launchDate: String!) {
    createMergedTask(firmId: $firmId, pmItemKeyJsons: $pmItemKeyJsons, launchDate: $launchDate) {
      ...FirmProperties
    }
  }
  ${firmProperties}
`

interface LaunchDateSectionProps {
  firmId: number
  onUpdate: () => void
}

interface LaunchDateSectionState {
  selectedDateType: BulkUpdateOp
  selectedTaskOrPropertyIds: Set<string | number>
  selectedTab: Tab
  creating: 'tasks' | 'merged'
  errorDateType: string
  errorDate: string
  errorTasksOrProperties: string
  submitting: boolean
  saveSuccess: boolean
  updatedTasks: LeasingTask[]
  createdProperties: PmProp[]
}

type LaunchDateSectionImplProps = LaunchDateSectionProps &
  FetchTasksAndPropertiesQueryVariables &
  QueryResult<FetchTasksAndPropertiesQuery, FetchTasksAndPropertiesQueryVariables>

class LaunchDateSectionImpl extends React.PureComponent<LaunchDateSectionImplProps, LaunchDateSectionState> {
  constructor(props: LaunchDateSectionImplProps) {
    super(props)

    this.state = {
      selectedDateType: BulkUpdateOp.LAUNCH_DATE,
      selectedTaskOrPropertyIds: new Set<string | number>(),
      selectedTab: Tab.ACTIVE,
      creating: 'tasks',
      errorDateType: '',
      errorDate: '',
      errorTasksOrProperties: '',
      submitting: false,
      saveSuccess: false,
      updatedTasks: [] as LeasingTask[],
      createdProperties: [] as PmProp[],
    }
  }

  private dateRef = React.createRef<HTMLInputElement>()

  private onselectedTaskOrPropertyIdsChange = (selectedTaskOrPropertyIds: Set<string | number>) =>
    this.setState({ selectedTaskOrPropertyIds })
  private keyFn = (r: LeasingTask | PmProp) => r.id

  private validateLaunchDate = (launchDate: string) => {
    if (validate.isoDate().validate(launchDate).error) {
      return 'launch date required: YYYY-MM-DD'
    } else if (new Date() >= new Date(launchDate)) {
      return 'must be in the future'
    }
    return ''
  }

  private onSaveUpdate = () => {
    const { selectedTab } = this.state

    if (selectedTab === Tab.PROPERTIES) {
      this.createTasks()
    } else {
      this.updateTasks()
    }
  }

  private createTasks = () => {
    const { onUpdate, data, firmId } = this.props
    const { selectedTaskOrPropertyIds, selectedDateType, creating } = this.state

    const props = data?.firm?.pmPropertiesWithoutTasks?.filter(p => selectedTaskOrPropertyIds.has(p.id)) || []
    const pmItemKeyJsons = props.map(p => p.auditKeyJson)

    this.setState({ submitting: true })

    let errorDateType = ''
    let errorTasksOrProperties = ''
    let errorDate = ''

    if (selectedDateType !== BulkUpdateOp.LAUNCH_DATE) {
      errorDateType = 'can only offboard properties with a task'
    }

    if (pmItemKeyJsons.length === 0) {
      errorTasksOrProperties = 'must select at least one property to create'
    }

    const dateInput = this.dateRef.current!.value

    if (dateInput.length === 0) {
      errorDate = 'must enter a launch date'
    } else {
      errorDate = this.validateLaunchDate(dateInput)
    }

    this.setState({ errorDateType, errorTasksOrProperties, errorDate })

    if (errorDateType || errorTasksOrProperties || errorDate) {
      this.setState({ submitting: false })
      return
    }

    this.setState({ createdProperties: props })

    const mutation = creating === 'tasks' ? createTasksMutation : createMergedTaskMutation

    apolloWebClient
      .mutate({
        mutation,
        variables: {
          firmId,
          pmItemKeyJsons,
          launchDate: dateInput,
        },
      })
      .then(() => {
        onUpdate()
        this.setState({ submitting: false, saveSuccess: true })
      })
      .catch((err: ApolloError) => {
        this.setState({ submitting: false })
        handleError(err)
      })
  }

  /**
   * Execute the bulk update by making a graphql mutation. Handle errors appropriately.
   */
  private updateTasks = () => {
    const { onUpdate, data } = this.props
    const { selectedTaskOrPropertyIds, selectedDateType } = this.state

    this.setState({ submitting: true })

    const tasks = data?.firm?.tasks?.filter(t => selectedTaskOrPropertyIds.has(t.id)) || []
    let errorDate = ''
    let errorTasksOrProperties = ''

    if (tasks.length === 0) {
      errorTasksOrProperties = 'must select at least one task to update'
    }

    const taskNames = tasks.map(t => t.displayName).join('\n')

    // empty value means clear
    const inputVal = this.dateRef.current?.value
    const value = inputVal == null || inputVal.length === 0 ? null : inputVal

    if (value != null) {
      errorDate = this.validateLaunchDate(value)
    }

    this.setState({ errorDate, errorTasksOrProperties })

    if (errorDate || errorTasksOrProperties) {
      this.setState({ submitting: false })
      return
    }

    if (value == null) {
      if (!confirm(`Are you sure you want to clear ${selectedDateType}?\n\n${taskNames}`)) {
        return
      }
    } else {
      if (!confirm(`Are you sure you want to bulk update ${selectedDateType} to '${value}'?\n\n${taskNames}`)) {
        return
      }
    }
    bulkUpdateTasks({
      taskIds: tasks.map(t => t.id),
      field: selectedDateType,
      value,
    })
      .then(res => {
        onUpdate()
        this.setState({ saveSuccess: true, submitting: false, updatedTasks: res.data!.bulkUpdateTasks })
      })
      .catch((e: ApolloError) => {
        handleError(e)
        this.setState({ errorDate: e.message, submitting: false })
      })
  }

  private dateTypeToggleOnClick = (vals: string[]) => {
    this.setState({ selectedDateType: BulkUpdateOp[vals[0] as keyof typeof BulkUpdateOp] })
  }

  private tabToggleOnClick = (vals: string[]) => {
    this.setState({ selectedTab: Tab[vals[0] as keyof typeof Tab] })
  }

  private dateTypeOptions = [
    { key: BulkUpdateOp.LAUNCH_DATE, name: '🚀  Launch' },
    { key: BulkUpdateOp.OFFBOARD_DATE, name: '👋  Offboard' },
  ]

  private tabOptions = [
    { key: Tab.ACTIVE, name: 'Active' },
    { key: Tab.ARCHIVED, name: 'Archived' },
    { key: Tab.PROPERTIES, name: 'Properties without a task' },
  ]

  private renderTasksTable = (isArchived: boolean, tasks: LeasingTask[]) => {
    if (tasks == null) {
      return null
    }

    const tasksToDisplay = tasks.filter(t => t.isArchived === isArchived)

    return (
      <TasksTable
        tasks={tasksToDisplay}
        as="TableMultiselect"
        widths={['230px', null, null]}
        alignments={['left', 'right', 'right']}
        onChange={this.onselectedTaskOrPropertyIdsChange}
      />
    )
  }

  private renderPropertiesTable = (properties: PmProp[]) => {
    const sortedProps = properties.slice().sort(
      lex(
        (r: PmProp) => (r.cleanAddress ? (getState(r.cleanAddress) || 'zzz') + ' ' + r.cleanAddress : 'zzz'),
        (r: PmProp) => r.id
      )
    )
    return (
      <TableMultiselect
        rows={sortedProps}
        columns={this.propCols}
        headers={['Address', 'Property', '# units']}
        widths={[null, null, '44px']}
        alignments={['left', 'left', 'right']}
        onChange={this.onselectedTaskOrPropertyIdsChange}
        keyFn={this.keyFn}
      />
    )
  }

  private colAddr = (r: PmProp) => (
    <EllipsisDiv title={r.cleanAddress || undefined}>
      {getState(r.cleanAddress) || '–'} <ColoredSpan color="silver">{r.cleanAddress}</ColoredSpan>
    </EllipsisDiv>
  )

  private colDisplayName = (r: PmProp) => (
    <EllipsisDiv title={r.displayName}>
      {r.auditKeyJson.includes('"APPFOLIO"') ? <Link href={r.pmUrl}>{r.displayName}</Link> : r.displayName}
    </EllipsisDiv>
  )

  private colNumUnits = (r: PmProp) => r.numUnits.toString()

  private propCols = [this.colAddr, this.colDisplayName, this.colNumUnits]

  private checkboxOnClick = () => {
    const { creating } = this.state
    if (creating === 'tasks') {
      this.setState({ creating: 'merged' })
    } else {
      this.setState({ creating: 'tasks' })
    }
  }

  private renderLaunchDateForm = () => {
    const { data, firmId } = this.props
    const { selectedDateType, selectedTab, submitting, errorDateType, errorDate, errorTasksOrProperties, creating } =
      this.state

    if (firmId == null) {
      return null
    }

    if (data == null || data.firm == null || data.firm.tasks == null) {
      return null
    }

    return (
      <>
        <H5>Launching or offboarding?</H5>
        <Spacer height={16} />
        <Toggle options={this.dateTypeOptions} value={selectedDateType} onSelect={this.dateTypeToggleOnClick} />
        {errorDateType && renderFieldError(errorDateType)}
        <Spacer height={32} />
        <H5>What’s the new date?</H5>
        <Spacer height={16} />
        <InputMed hasError={errorDate !== ''} placeholder="2020-05-01" ref={this.dateRef} type="date" />
        {errorDate && renderFieldError(errorDate)}
        <Spacer height={64} />
        <H3>For which properties?</H3>
        <Spacer height={16} />
        <Toggle options={this.tabOptions} value={selectedTab} onSelect={this.tabToggleOnClick} />
        <Spacer height={16} />
        {selectedTab === Tab.ACTIVE
          ? this.renderTasksTable(false, data.firm.tasks)
          : selectedTab === Tab.ARCHIVED
          ? this.renderTasksTable(true, data.firm.tasks)
          : this.renderPropertiesTable(data.firm.pmPropertiesWithoutTasks)}

        {errorTasksOrProperties && <ErrorText>{errorTasksOrProperties}</ErrorText>}
        {selectedTab === Tab.PROPERTIES && (
          <>
            <Spacer height={16} />
            <Checkbox check={creating === 'merged' ? 'checked' : 'unchecked'} onClick={this.checkboxOnClick} />
            {'   '}
            <SmallerTextSpan as="span">Create merged task</SmallerTextSpan>
          </>
        )}
        <Spacer height={32} />
        <AlignRight>
          <Button disabled={submitting} onClick={this.onSaveUpdate}>
            Save Update
          </Button>
        </AlignRight>
        <Spacer width={16} />
      </>
    )
  }

  private renderCreateTasksSuccess = () => {
    const { data } = this.props
    const { createdProperties, creating } = this.state

    if (data?.firm?.pmPropertiesWithoutTasks == null) {
      return null
    }

    return (
      <>
        <H1>Thank you!</H1>
        {createdProperties.length > 0 && (
          <>
            <H3 color="silver">
              We were able to create {creating === 'tasks' ? 'tasks' : 'a merged task'} for following properties:
            </H3>
            <Spacer height={16} />
            {createdProperties.map(p => (
              <span key={p.id}>
                <H3 color="steel">{p.displayName}</H3>
                <Spacer height={16} />
              </span>
            ))}
          </>
        )}
        <Spacer height={16} />
      </>
    )
  }

  private renderLaunchDateSuccess = () => {
    const { data } = this.props
    const { selectedDateType, updatedTasks, selectedTaskOrPropertyIds } = this.state

    const date = this.dateRef.current?.value

    if (data == null || data.firm == null) {
      return null
    }

    const tasksNotUpdated = getTasksNotUpdated(data.firm.tasks, selectedTaskOrPropertyIds, updatedTasks)

    let text = ''
    if (date == null || date === '') {
      text = `We were able to clear the \`${formatEnum(selectedDateType)}\` at the following properties:`
    } else {
      text = `We were able to update the \`${formatEnum(
        selectedDateType
      )}\` to \`${date}\` at the following properties:`
    }

    return (
      <>
        <H1>Thank you!</H1>
        <Spacer height={64} />
        {updatedTasks.length > 0 && (
          <>
            <H3 color="silver">{text}</H3>
            <Spacer height={16} />
            {renderTasksList(updatedTasks)}
          </>
        )}
        {tasksNotUpdated.length > 0 && (
          <>
            <H3 color="silver">We were not able to update `{selectedDateType}` for following properties:</H3>
            <Spacer height={16} />
            {renderTasksList(tasksNotUpdated)}
          </>
        )}
        <Spacer height={16} />
      </>
    )
  }

  render() {
    const { saveSuccess, selectedTab } = this.state

    return saveSuccess
      ? selectedTab === Tab.PROPERTIES
        ? this.renderCreateTasksSuccess()
        : this.renderLaunchDateSuccess()
      : this.renderLaunchDateForm()
  }
}

export default function LaunchDateSection(props: LaunchDateSectionProps) {
  const { firmId } = props

  if (firmId == null) {
    return null
  }

  const res = useQuery<FetchTasksAndPropertiesQuery, FetchTasksAndPropertiesQueryVariables>(
    fetchTasksAndPropertiesQuery,
    {
      variables: { firmId },
    }
  )

  if (res.loading || res.error) {
    return null
  }

  return (
    <InlineErrorBoundary componentName="LaunchDateSection">
      <LaunchDateSectionImpl {...props} {...res} />
    </InlineErrorBoundary>
  )
}
